import { useTranslation } from 'react-i18next'

import { Text } from '@mercai/clever'

import {
  HistoryTimelineItemProps,
  HistoryTimelineProps,
} from './history-timeline.type'

import { useFileSign } from '@/services'
import { HistoryColumnsModifiedTypeEnum } from '@/types'
import { useEffect, useRef, useState } from 'react'
import { getFormattedValue } from './history-timeline.constants'
import styles from './history-timeline.module.css'

const HistoryTimelineItem = ({
  children,
  isLast,
}: HistoryTimelineItemProps) => {
  const ref = useRef<HTMLDivElement>(null)

  const [lineHeight, setLineHeight] = useState('100%')

  useEffect(() => {
    const getHeight = () => {
      if (ref?.current?.clientHeight) {
        return setLineHeight(`calc(${ref?.current?.clientHeight}px - 1rem)`)
      }

      return setLineHeight('100%')
    }
    getHeight()

    window.addEventListener('resize', getHeight)

    return () => {
      window.removeEventListener('resize', getHeight)
    }
  }, [])

  return (
    <div className={styles.item}>
      <div className={styles['marker-container']}>
        <div className={styles.marker} />
        {!isLast && (
          <div
            className={styles.line}
            style={{
              height: lineHeight,
            }}
          />
        )}
      </div>
      <div className={!isLast ? styles.content : ''} ref={ref}>
        {children}
      </div>
    </div>
  )
}

export const HistoryTimeline = ({
  items,
  coreEntity,
  translationKey,
}: HistoryTimelineProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation(translationKey)

  const { mutateAsync } = useFileSign()

  const showFile = async (url: string) => {
    if (!url) return null

    if (url.includes('http')) {
      return window.open(url, '_blank')
    }

    const response = await mutateAsync({
      filePath: url || '',
    })

    return response?.data?.signedUrl
      ? window.open(response?.data?.signedUrl, '_blank')
      : null
  }

  const FormattedData = ({
    entity,
    columnKey,
    oldValue,
    newValue,
    type,
  }: {
    entity: string
    columnKey: string
    oldValue: string | number | boolean | null
    newValue: string | number | boolean | null
    type: keyof typeof HistoryColumnsModifiedTypeEnum
  }) => {
    if (type === 'object') {
      return (
        <Text variant="text-small">
          <pre>{JSON.stringify(newValue, null, 2)}</pre>
        </Text>
      )
    }

    if (type && type === 'file' && newValue) {
      return (
        <Text
          variant="text-small"
          onClick={() => showFile(newValue as string)}
          className={styles['file-link']}
        >
          {tEnums('history_timeline_file')}
        </Text>
      )
    }

    return (
      <>
        <Text variant="text-small">
          {oldValue
            ? getFormattedValue(entity, columnKey, type, oldValue, tEnums)
            : tEnums('history_timeline_not_informed')}
        </Text>
        <Text variant="text-small">→</Text>
        <Text variant="text-small">
          {newValue
            ? getFormattedValue(entity, columnKey, type, newValue, tEnums)
            : tEnums('history_timeline_not_informed')}
        </Text>
      </>
    )
  }

  return (
    <div className={styles.timeline}>
      {items?.map((item, index) => (
        <HistoryTimelineItem key={index} isLast={index === items.length - 1}>
          <div className={styles['date-container']}>
            <Text variant="title-small" className={styles['user-name']}>
              {item?.userName || tEnums('history_timeline_system')}
              {item?.reference ? ` - #${item?.reference}` : ''}
            </Text>
            {!item?.columnsModified ? (
              <Text variant="title-small">
                {t(`${coreEntity}_${item.entity}_${item.type}`)}
              </Text>
            ) : (
              <div className={styles['columns-modified']}>
                {item?.columnsModified.map((column, index) => (
                  <div
                    key={index}
                    className={styles['columns-modified-container']}
                  >
                    <Text variant="title-small">
                      {t(
                        `${coreEntity}_${item.entity}_${item.type}_${column.columnKey}`,
                      )}
                    </Text>
                    <div className={styles.divider} />
                    <FormattedData
                      entity={item?.entity}
                      columnKey={column?.columnKey}
                      oldValue={column?.oldValue}
                      newValue={column?.newValue}
                      type={column?.type}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </HistoryTimelineItem>
      ))}
    </div>
  )
}
